import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Row,
  Col,
  Card,
  Form,
  Radio,
  Divider,
  Spin,
  message,
  Checkbox,
  Space,
  Modal,
} from "antd";
import { CheckOutlined, ClockCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import { CardContainer } from "../../components/Card/CardContainer";
import { useMessageContext } from "../../context/MessageProvider";
import Text from "../../components/Text/Text";
import { useEffectOnce } from "react-use";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import Input from "../../components/Input/Input";
import Buttons from "../../components/Button/Button";
import StepAntd from "../../components/StepAntd/StepAntd";
import color from "../../resource/color";
import ImgCrop from "../../components/ImgCrop/ImgCrop";
import { ImageWithDeleteButton, UploadIcon } from "../../components/Image/Image";
import { RcFile } from "antd/lib/upload";
import { FlexCol, FlexRow } from "../../components/Container/Container";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { newsTypes } from "../../definitions/news";
import ImageResize from "quill-image-resize-module-react";
import { inputNumberValidator } from "../../utility/validator";
import { createNews, getNewsById, updateNews } from "../../datasource/News";
import DatePicker, { TimePicker } from "../../components/DatePicker/DatePicker";
import dayjs from "dayjs";
import { resizeFile } from "../../utility/uploadHelper";
import { NewsAppPreview } from "./CreateNewsStep/NewsAppPreview";
import {
  fetchAllNewsShops,
  NewsShop,
  NewsStepTwoShop,
  toNewsShopPayload,
} from "./CreateNewsStep/NewsStepTwoShop";
import { NewsStepThree } from "./CreateNewsStep/NewsStepThree";
import { imageSize, QuillContainer, UploadArea, UploadBox } from "./CreateNewsStep/NewsStyled";
import {
  clearNewsDraft,
  dataUrlToFile,
  getNewsDraftKey,
  hasDraftData,
  loadNewsDraft,
  saveNewsDraft,
} from "./CreateNewsStep/newsDraft";

Quill.register("modules/imageResize", ImageResize);

const imgCropProps = {
  modalTitle: "ปรับขนาดรูปภาพ",
  modalOk: "ยืนยัน",
  modalCancel: "ยกเลิก",
};

const stepItems = [{ title: "ข้อมูลข่าวสาร" }, { title: "ร้านค้า" }, { title: "ตรวจสอบ" }];
const isEmptyQuillValue = (value?: string) =>
  !value ||
  !value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim();

const toDayjs = (value?: any) => {
  const date = value ? dayjs(value) : undefined;
  return date?.isValid() ? date : undefined;
};

const toIsoString = (value?: any) => toDayjs(value)?.toISOString();
const toPublishTime = (startDate?: any, startTime?: any) => {
  const date = toDayjs(startDate);
  const time = toDayjs(startTime);
  if (!date || !time) return undefined;
  return toDayjs(`${date.format("YYYY-MM-DD")} ${time.format("HH:mm")}:00.000`);
};
const isActiveStatus = (status?: string) => status !== "DRAFT" && status !== "INACTIVE";

const getBase64 = (img: RcFile, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

export const NewsEdit: React.FC = (props: any) => {
  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const isEdit = pathSplit[2] === "edit";
  const id = pathSplit[3];

  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company, firstname, lastname } = userProfile;

  const [form] = Form.useForm();
  const [vidForm] = Form.useForm();
  const { messageApi } = useMessageContext();
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState();

  const [loading, setLoading] = useState(false);
  const [imgFile, setImgFile] = useState<any>();
  const [imgUrl, setImgUrl] = useState<any>();
  const [uploading, setUploading] = useState(false);
  const [topic, setTopic] = useState("หัวข้อข่าว");
  const [content, setContent] = useState("-");
  const [status, setStatus] = useState<string>();
  const [hasPublished, setHasPublished] = useState(false);

  const [showVideoModal, setVideoModal] = useState(false);
  const [vidIndex, setVidIndex] = useState();

  const [step, setStep] = useState(0);
  const [shops, setShops] = useState<NewsShop[]>([]);
  const [shopError, setShopError] = useState<string>();
  const [imgError, setImgError] = useState<string>();
  const [shopStepMounted, setShopStepMounted] = useState(false);

  const [shopsLoaded, setShopsLoaded] = useState(!isEdit);
  const [loadingShops, setLoadingShops] = useState(false);
  const fetchingShopsRef = useRef(false); 
  const draftKey = useMemo(() => getNewsDraftKey(isEdit, id), [isEdit, id]);
  const [draftReady, setDraftReady] = useState(false);
  const [formTick, setFormTick] = useState(0);

  const restoredRef = useRef(false); 
  const quillRef = useRef<any>(null);

  const onSubmitVideo = () => {
    const { width, height, url } = vidForm.getFieldsValue();
    const quill = quillRef?.current?.getEditor();
    // test url https://www.youtube.com/embed/0utfT0nbuTA?si=Mgq1KHKJypkOdoyD
    // test url https://www.youtube.com/watch?v=KMoe95BTwew
    // test url https://youtu.be/KMoe95BTwew?si=RJek2YfcpY-B-njR
    const src = url?.replace("youtu.be", "www.youtube.com/embed/").replace("watch?v=", "embed/");
    const iframeStr = `<iframe width="${width}" height="${height}" style="overflow:hidden;" src="${src}" frameborder="0" clipboard-write; encrypted-media; ></iframe>`;
    quill.clipboard.dangerouslyPasteHTML(vidIndex, iframeStr);

    vidForm.resetFields();
    setVideoModal(false);
  };

  const videoHandler = useMemo(() => {
    return () => {
      const quill = quillRef?.current?.getEditor();
      const range = quill.getSelection();
      setVidIndex(range.index);
      setVideoModal(true);
    };
  }, []);

  const modules = {
    toolbar: {
      handlers: {
        video: videoHandler,
      },
      container: [
        ["bold", "italic", "link", "image", "video"],
        [{ size: ["small", false, "large", "huge"] }],
        [{ align: "" }, { align: "center" }, { align: "right" }],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
    },
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  };

  useEffectOnce(() => {
    const init = async () => {
      if (isEdit) await fetchData();
      restoreDraft();
      setDraftReady(true);
    };
    init();
  });

  const restoreDraft = () => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const draft = loadNewsDraft(draftKey);
    if (!draft || !hasDraftData(draft)) return;

    form.setFieldsValue({
      topic: draft.values?.topic,
      type: draft.values?.type,
      app: draft.values?.app,
      content: draft.content,
      startDate: toDayjs(draft.values?.startDate),
      startTime: toDayjs(draft.values?.startTime),
    });

    if (draft.topic) setTopic(draft.topic);
    if (draft.content) setContent(draft.content);
    if (draft.status) setStatus(draft.status);
    if (draft.shopsLoaded) setShopsLoaded(true);
    setShops(draft.shops || []);
    if (draft.imgUrl) {
      setImgUrl(draft.imgUrl);
      const file = dataUrlToFile(draft.imgUrl, draft.imgFile?.name, draft.imgFile?.type);
      if (file) setImgFile(file);
    }
    if (draft.step > 0) setShopStepMounted(true);
    setStep(draft.step || 0);

    if (draft.imgSkipped) message.warning("รูปภาพมีขนาดใหญ่เกินไป กรุณาอัปโหลดรูปภาพอีกครั้ง");
  };

  const saveDraft = () => {
    const { topic: topicValue, type, app, startDate, startTime } = form.getFieldsValue(true);
    saveNewsDraft(draftKey, {
      step,
      status,
      topic,
      content,
      imgUrl,
      imgFile: imgFile ? { name: imgFile.name, type: imgFile.type } : undefined,
      shops,
      shopsLoaded,
      values: {
        topic: topicValue,
        type,
        app,
        startDate: toIsoString(startDate),
        startTime: toIsoString(startTime),
      },
      savedAt: Date.now(),
    });
  };

  useEffect(() => {
    if (!draftReady) return;
    const timer = setTimeout(saveDraft, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReady, step, status, topic, content, imgUrl, imgFile, shops, shopsLoaded, formTick]);
  useEffect(() => {
    const formPath = window.location.pathname;
    return () => {
      if (window.location.pathname === formPath) return;
      clearNewsDraft(draftKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step >= 1) setShopStepMounted(true);
  }, [step]);

  useEffect(() => {
    if (!draftReady || shopsLoaded || loadingShops || step < 1) return;
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReady, shopsLoaded, step]);

  const fetchShops = async () => {
    if (fetchingShopsRef.current) return;
    try {
      fetchingShopsRef.current = true;
      setLoadingShops(true);
      setShops(await fetchAllNewsShops(id));
      setShopsLoaded(true);
    } catch (e) {
      console.log("fetch news shops error", e);
      messageApi.error("โหลดร้านค้าที่เห็นข่าวไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      fetchingShopsRef.current = false;
      setLoadingShops(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { responseData } = await getNewsById(id);
      setNewsData(responseData);

      const app: string[] = [];
      if (responseData?.isShowOnSaleApp) app.push("isShowOnSaleApp");
      if (responseData?.isShowOnShopApp) app.push("isShowOnShopApp");
      form.setFieldsValue({
        ...responseData,
        app,
        startDate: toDayjs(responseData?.publishTime),
        startTime: toDayjs(responseData?.publishTime),
      });

      setStatus(responseData?.status);
      setHasPublished(responseData?.status === "PUBLISHED" || responseData?.status === "INACTIVE");
      setImgUrl(responseData?.imageUrl);
      setTopic(responseData?.topic);

      // set Content
      setContent(responseData?.contentFile);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const hiddenStatus = isEdit && hasPublished ? "DRAFT" : "INACTIVE";
  const disabledStatus = isEdit && hasPublished ? "WAITING" : undefined;
  const resolveSubmitStatus = (publishTime?: dayjs.Dayjs) => {
    const current = status || "PUBLISHED";
    if (!isEdit || current !== "WAITING") return current;
    return publishTime?.isAfter(dayjs()) ? "WAITING" : "PUBLISHED";
  };

  const updateData = async () => {
    const { topic, type, app, startDate, startTime } = form.getFieldsValue();
    const data = new FormData();
    const quill = quillRef.current?.getEditor();
    const contentHtml = quill ? quill.root.innerHTML : content;
    const content_ = new Blob([contentHtml], { type: "text/html charset=utf-8" });
    const publishTime = toPublishTime(startDate, startTime);
    const submitStatus = resolveSubmitStatus(publishTime);

    if (isEdit) data.append("newsId", id);
    data.append("company", company);
    data.append("topic", topic);
    data.append("isShowOnSaleApp", app?.includes("isShowOnSaleApp"));
    data.append("isShowOnShopApp", app?.includes("isShowOnShopApp"));
    data.append("status", submitStatus);
    data.append("type", type);
    data.append("createdBy", firstname + " " + lastname);
    data.append("newsShops", JSON.stringify(JSON.stringify(toNewsShopPayload(shops))));

    if (publishTime) data.append("publishTime", publishTime.toISOString());
    if (imgFile) {
      const resizedImage = await resizeFile(imgFile, 2);
      data.append("image", resizedImage || imgFile);
    }
    if (content_) data.append("content", content_);
    const cb = (res) => {
      if (res.success) {
        messageApi.open({
          type: "success",
          icon: <CheckOutlined style={{ color: color.success }} />,
          content: "บันทึกสำเร็จ",
        });
        clearNewsDraft(draftKey);
        navigate(`/${pathSplit[1]}/list`);
      } else {
        messageApi.error(res.userMessage || "บันทึกข้อมูลไม่สำเร็จ");
      }
    };
    const cbCatch = (e) => {
      console.log("save news error", e?.response?.data || e);
      messageApi.error(e?.response?.data?.userMessage || "บันทึกข้อมูลไม่สำเร็จ");
    };
    const cbFinal = () => setUploading(false);

    try {
      setUploading(true);
      if (isEdit) {
        await updateNews(data).then(cb).catch(cbCatch).finally(cbFinal);
      } else {
        await createNews(data).then(cb).catch(cbCatch).finally(cbFinal);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setUploading(false);
    }
  };

  const onConfirmSubmit = () => {
    Modal.confirm({
      icon: <></>,
      title: <Text level={2}>ต้องการยืนยันการ{isEdit ? "แก้ไข" : "สร้าง"}ข่าวสาร</Text>,
      content: (
        <Text level={5} color='Text3'>
          โปรดตรวจสอบรายละเอียดที่คุณต้องการ{isEdit ? "แก้ไข" : "สร้าง"}
          ข่าวสารก่อนเสมอเพราะอาจส่งผลต่อการแสดงผลข่าวสารในระบบแอปพลิเคชัน
        </Text>
      ),
      width: 524,
      cancelText: "ยกเลิก",
      okText: "ยืนยัน",
      onOk: updateData,
    });
  };

  const onNext = async () => {
    if (step === 0) {
      const hasImage = !!imgFile || !!imgUrl;
      setImgError(hasImage ? undefined : "กรุณาอัปโหลดรูปภาพ");
      try {
        await form.validateFields();
        if (!hasImage) return;
        setStep(1);
      } catch (e) {
        console.log("error step 1", e);
      }
      return;
    }
    if (step === 1) {
      if (loadingShops) return;
      if (shops.length <= 0) {
        setShopError("กรุณาเพิ่มร้านค้า");
        return;
      }
      setShopError(undefined);
      setStep(2);
      return;
    }
    onConfirmSubmit();
  };

  const onPrev = () => setStep(step - 1);

  const renderPageTitle = () => {
    return (
      <PageTitleNested
        title={isEdit ? "แก้ไขข่าวสาร" : "สร้างข่าวสาร"}
        showBack
        onBack={() => {
          clearNewsDraft(draftKey);
          navigate(`/${pathSplit[1]}/list`);
        }}
        extra={<StepAntd current={step} items={stepItems} />}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "รายการข่าวสาร", path: `/${pathSplit[1]}/list` },
              {
                text: isEdit ? "แก้ไขข่าวสาร" : "สร้างข่าวสาร",
                path: window.location.pathname,
              },
            ]}
          />
        }
      />
    );
  };

  const renderStepOne = () => (
    <Row gutter={16}>
      <Col style={{ width: "calc(100% - 320px)" }}>
        <Text level={5} fontWeight={700}>
          รายละเอียดข้อมูลข่าวสาร
        </Text>
        <FlexRow style={{ padding: "24px 0px 16px" }}>
          <FlexCol style={{ marginRight: 60 }}>
            <Form.Item noStyle name='image' valuePropName='file'>
              <ImgCrop aspect={1} {...imgCropProps}>
                <UploadBox
                  listType='picture-card'
                  maxCount={1}
                  beforeUpload={(file) => {
                    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
                    if (!isJpgOrPng) {
                      message.error("You can only upload JPG/PNG file!");
                      return true;
                    }
                    setImgFile(file);
                    setImgError(undefined);
                    getBase64(file as RcFile, (url) => {
                      setImgUrl(url);
                    });
                  }}
                  customRequest={() => {
                    console.log("customRequest");
                  }}
                  onChange={({ file }: any) => {
                    return "success";
                  }}
                  onRemove={() => {
                    setImgFile(undefined);
                    setImgUrl(undefined);
                  }}
                  showUploadList={false}
                  disabled={!!imgFile || !!imgUrl}
                >
                  {!imgFile && !imgUrl ? (
                    <UploadArea
                      style={{
                        width: imageSize,
                        height: imageSize,
                      }}
                    >
                      {UploadIcon}
                    </UploadArea>
                  ) : (
                    <Spin spinning={loading}>
                      <ImageWithDeleteButton
                        width={imageSize}
                        height={imageSize}
                        src={imgUrl}
                        handleDelete={() => {
                          setImgFile(undefined);
                          setImgUrl(undefined);
                        }}
                      />
                    </Spin>
                  )}
                </UploadBox>
              </ImgCrop>
            </Form.Item>
          </FlexCol>
          <FlexCol>
            <Text level={6}>
              รูปภาพประกอบข่าวสาร&nbsp;<span style={{ color: color.error }}>*</span>
            </Text>
            {imgError && (
              <Text color='error' fontSize={12} fontFamily='Sarabun'>
                {imgError}
              </Text>
            )}
            <Text level={6} color='Text3'>
              JPG or PNG. Size of
              <br />
              1200*1200px
              <br />
              1:1
            </Text>
          </FlexCol>
        </FlexRow>
        <br />
        <Form.Item
          name='topic'
          label='หัวข้อข่าว'
          rules={[
            {
              required: true,
              message: "กรุณาระบุหัวข้อข่าว",
            },
            {
              max: 50,
              message: "หัวข้อข่าวต้องมีความยาวไม่เกิน 50 ตัวอักษร",
            },
          ]}
        >
          <Input
            placeholder='ระบุหัวข้อข่าว'
            autoComplete='off'
            onChange={(e) => setTopic(e.target.value)}
          />
        </Form.Item>
        <Form.Item
          name='content'
          label='รายละเอียดข่าว'
          required
          rules={[
            {
              validator(rule, value) {
                const html = quillRef.current?.getEditor()?.root?.innerHTML ?? value;
                if (isEmptyQuillValue(html)) {
                  return Promise.reject("กรุณาระบุรายละเอียดข่าว");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <QuillContainer>
            <ReactQuill
              ref={quillRef}
              modules={modules}
              onChange={(c) => {
                setContent(c);
                form.setFieldValue("content", c);
              }}
              value={content}
            />
          </QuillContainer>
        </Form.Item>
        <Form.Item
          name='type'
          label={
            <Text level={6} fontWeight={700}>
              หมวดหมู่
            </Text>
          }
          initialValue={newsTypes.NEWS.key}
          required
        >
          <Radio.Group>
            <br />
            <Space direction='vertical' style={{ gap: 0 }}>
              {Object.values(newsTypes).map(({ key, name }) => (
                <Radio value={key} key={key}>
                  <Text fontFamily='Sarabun' fontSize={14}>
                    {name}
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name='app'
          label={
            <Text level={6} fontWeight={700}>
              แอปพลิเคชัน
            </Text>
          }
          required
          rules={[
            {
              validator(rule, value) {
                if (!value || value.length <= 0) {
                  return Promise.reject("กรุณาเลือกแอปพลิเคชัน");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Checkbox.Group
            options={[
              {
                label: "Shop App",
                value: "isShowOnShopApp",
              },
              {
                label: "Sale App",
                value: "isShowOnSaleApp",
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          name='status'
          label={
            <Text level={6} fontWeight={700}>
              สถานะ
            </Text>
          }
          required
        >
          <Radio checked={isActiveStatus(status)} onChange={() => setStatus("PUBLISHED")}>
            <Text fontFamily='Sarabun' fontSize={14}>
              เปิดใช้งาน
            </Text>
          </Radio>
          <br />
          <FlexCol>
            {[
              {
                name: (
                  <Text fontFamily='Sarabun' fontSize={14}>
                    เผยแพร่ทันที
                  </Text>
                ),
                key: "PUBLISHED",
                isSub: true,
              },
              {
                name: (
                  <Text fontFamily='Sarabun' fontSize={14}>
                    ตั้งเวลาเผยแพร่
                  </Text>
                ),
                key: "WAITING",
                isSub: true,
                extra: (
                  <Row gutter={16} style={{ padding: "4px 0" }}>
                    <Col span={12}>
                      <Form.Item
                        name='startDate'
                        required
                        rules={[
                          {
                            required: true,
                            message: "กรุณาเลือกวันที่เผยแพร่",
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          disabledDate={(current) => {
                            return current && current.isBefore(dayjs().subtract(0, "day"));
                          }}
                          suffixIcon={<CalendarOutlined />}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name='startTime'
                        rules={[
                          {
                            required: true,
                            message: "กรุณาเลือกเวลาเผยแพร่",
                          },
                        ]}
                        initialValue={dayjs("00:00", "HH:mm")}
                      >
                        <TimePicker allowClear={false} suffixIcon={<ClockCircleOutlined />} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                name: (
                  <Text fontFamily='Sarabun' fontSize={14}>
                    แบบร่าง
                  </Text>
                ),
                key: "DRAFT",
              },
              {
                name: (
                  <Text fontFamily='Sarabun' fontSize={14}>
                    ปิดใช้งาน
                  </Text>
                ),
                key: "INACTIVE",
              },
            ]
              .filter(({ isSub, key }) => {
                if (key === hiddenStatus) return false;
                if (!isActiveStatus(status)) return !isSub;

                return true;
              })
              .map(({ key, name, isSub, extra }) => (
                <div style={{ ...(isSub ? { paddingLeft: 24 } : {}), width: "100%" }} key={key}>
                  <Radio
                    key={key}
                    checked={status === key}
                    disabled={key === disabledStatus}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStatus(key);
                      }
                    }}
                  >
                    {name}
                  </Radio>
                  {status === key && (
                    <>
                      <br />
                      {extra}
                    </>
                  )}
                </div>
              ))}
          </FlexCol>
        </Form.Item>
      </Col>
      <Col style={{ width: "320px" }}>
        <NewsAppPreview imgUrl={imgUrl} topic={topic} content={content} />
      </Col>
    </Row>
  );

  return loading ? (
    <div className='container '>
      <Card loading />
    </div>
  ) : (
    <Row justify={"space-between"} gutter={8}>
      <Col span={24}>
        <CardContainer>
          {renderPageTitle()}
          <Divider />
          <Form form={form} layout='vertical' onValuesChange={() => setFormTick((t) => t + 1)}>
            <div style={{ display: step === 0 ? "block" : "none" }}>{renderStepOne()}</div>
          </Form>
          {shopStepMounted && (
            <div style={{ display: step === 1 ? "block" : "none" }}>
              <NewsStepTwoShop
                company={company}
                shops={shops}
                setShops={setShops}
                errorMessage={shopError}
                setErrorMessage={setShopError}
                loading={loadingShops}
              />
            </div>
          )}
          {step === 2 && (
            <NewsStepThree
              values={form.getFieldsValue(true)}
              imgUrl={imgUrl}
              topic={topic}
              content={content}
              status={status}
              shops={shops}
            />
          )}
          <Divider />
          <Row align='middle' justify='space-between' gutter={12}>
            <Col span={3}>
              {step > 0 && <Buttons typeButton='primary-light' title='ย้อนกลับ' onClick={onPrev} />}
            </Col>
            <Col span={3}>
              <Buttons
                typeButton='primary'
                title={step === 2 ? "บันทึก" : "ถัดไป"}
                onClick={onNext}
                loading={uploading}
              />
            </Col>
          </Row>
        </CardContainer>
        <Modal
          title='เพิ่มวิดีโอ'
          open={showVideoModal}
          onCancel={() => setVideoModal(false)}
          footer={null}
        >
          <Form form={vidForm} layout='vertical' onFinish={onSubmitVideo}>
            <Form.Item
              name='width'
              label='ความกว้าง'
              required
              initialValue={280}
              rules={[
                {
                  message: "กรอกตัวเลข",
                  validator: inputNumberValidator,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name='height'
              label='ความสูง'
              required
              initialValue={168}
              rules={[
                {
                  message: "กรอกตัวเลข",
                  validator: inputNumberValidator,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name='url' label='Video URI' required rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <br />
            <Form.Item>
              <Row justify='end'>
                <Button type='primary' htmlType='submit'>
                  OK
                </Button>
              </Row>
            </Form.Item>
          </Form>
        </Modal>
      </Col>
    </Row>
  );
};
